import type { IMessage } from '@/modules/chat-matrix-fast/types/IChat'
import { looksLikeMatrixEventId } from '@/modules/chat-matrix-fast/services/matrixEventMapper'

export type MessageIdentityLookup = {
  id?: number | string
  matrixEventId?: string
  txnId?: string
}

export function getMessageIdentityKey(
  message: Pick<IMessage, 'id' | 'matrix_event_id' | 'matrix_txn_id'>,
): string {
  if (message.matrix_event_id) {
    return String(message.matrix_event_id)
  }
  if (message.matrix_txn_id) {
    return `txn:${message.matrix_txn_id}`
  }

  return `id:${message.id}`
}

export function findMessageIndex(
  messages: IMessage[],
  lookup: MessageIdentityLookup,
): number {
  const hint = lookup.matrixEventId?.trim()
  if (hint) {
    const byHint = messages.findIndex(
      m => m.matrix_event_id && String(m.matrix_event_id) === hint,
    )
    if (byHint !== -1) {
      return byHint
    }
  }

  const id = lookup.id
  if (id != null && looksLikeMatrixEventId(id)) {
    const byEventFromId = messages.findIndex(
      m => m.matrix_event_id && String(m.matrix_event_id) === String(id),
    )
    if (byEventFromId !== -1) {
      return byEventFromId
    }
  }

  const txnId = lookup.txnId?.trim()
  if (txnId) {
    const byTxn = messages.findIndex(
      m => m.matrix_txn_id && String(m.matrix_txn_id) === txnId,
    )
    if (byTxn !== -1) {
      return byTxn
    }
  }

  if (id == null) {
    return -1
  }

  const idStr = String(id)
  const byEvent = messages.findIndex(
    m => m.matrix_event_id && String(m.matrix_event_id) === idStr,
  )
  if (byEvent !== -1) {
    return byEvent
  }

  return messages.findIndex(m => String(m.id) === idStr)
}

export function assertSameMessage(
  a: Pick<IMessage, 'id' | 'matrix_event_id' | 'matrix_txn_id'>,
  b: Pick<IMessage, 'id' | 'matrix_event_id' | 'matrix_txn_id'>,
): boolean {
  const aEvent = a.matrix_event_id?.trim()
  const bEvent = b.matrix_event_id?.trim()
  if (aEvent && bEvent) {
    return aEvent === bEvent
  }

  const aTxn = a.matrix_txn_id?.trim()
  const bTxn = b.matrix_txn_id?.trim()
  if (aTxn && bTxn) {
    return aTxn === bTxn
  }

  return false
}

export function messagesShareNumericIdCollision(
  a: Pick<IMessage, 'id' | 'matrix_event_id' | 'matrix_txn_id'>,
  b: Pick<IMessage, 'id' | 'matrix_event_id' | 'matrix_txn_id'>,
): boolean {
  if (a.id !== b.id) {
    return false
  }
  if (assertSameMessage(a, b)) {
    return false
  }
  const aEvent = a.matrix_event_id?.trim()
  const bEvent = b.matrix_event_id?.trim()

  return Boolean(aEvent && bEvent && aEvent !== bEvent)
}

export function findMessage(
  messages: IMessage[],
  lookup: MessageIdentityLookup,
): IMessage | undefined {
  const index = findMessageIndex(messages, lookup)
  return index >= 0 ? messages[index] : undefined
}

export function isMessageAtOrBeforeReadMarker(
  message: Pick<IMessage, 'id' | 'matrix_event_id' | 'matrix_txn_id' | 'created_at'>,
  markerId?: number,
  markerEventId?: string,
  markerMessage?: Pick<IMessage, 'created_at' | 'matrix_event_id' | 'id'>,
): boolean {
  if (markerId == null && !markerEventId?.trim()) {
    return true
  }

  const markerEvent = markerEventId?.trim()
    || markerMessage?.matrix_event_id?.trim()
  const messageEvent = message.matrix_event_id?.trim()

  if (markerEvent && messageEvent) {
    if (messageEvent === markerEvent) {
      return true
    }
    const markerTime = Date.parse(markerMessage?.created_at ?? '')
    const messageTime = Date.parse(message.created_at ?? '')
    if (!Number.isNaN(markerTime) && !Number.isNaN(messageTime)) {
      return messageTime <= markerTime
    }
  }

  const markerTime = Date.parse(markerMessage?.created_at ?? '')
  const messageTime = Date.parse(message.created_at ?? '')
  if (!Number.isNaN(markerTime) && !Number.isNaN(messageTime)) {
    return messageTime <= markerTime
  }

  if (markerId != null && message.id != null) {
    return message.id <= markerId
  }

  return false
}
